import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CustomerDatabaseStore } from "../../../../store/main/CustomerDatabaseStore";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import UserExpenseReport from "./sub-component/UserExpenseReport";
import ManagerExpenseApproval from "./sub-component/ManagerExpenseApproval";
import Roles from "../../../../common/enums/Roles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const TAB_STORAGE_KEY = "expense_dashboard_tab";

const ExpenseDashBoardDesktop = () => {
  const { userDetails, userRole, decodeToken } = CustomerDatabaseStore();
  const [value, setValue] = useState<string>("user_expense_report");
  const [loading, setLoading] = useState(true);

  const isAreaManager = userRole.role === Roles.AREA_MANAGER;
  const isHeadquarterManager = userRole.role === Roles.HEADQUATER_MANAGER;

  const allowedDomains = [
    "goapptiv.com",
    "channelitix.com",
    "iconpharma.in",
    "goapptiv.in",
  ];

  const email = userDetails?.official_email || "";
  const emailDomain = email.split("@")[1];

  const isAllowedDomain = allowedDomains.includes(emailDomain);

  useEffect(() => {
    decodeToken();
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const storedTab = localStorage.getItem(TAB_STORAGE_KEY);

    if (!userRole?.role) return;

    const isAreaManager = userRole.role === Roles.AREA_MANAGER;

    if (storedTab === "manager_expense_approval" && isAreaManager) {
      setValue("user_expense_report");
      localStorage.setItem(TAB_STORAGE_KEY, "user_expense_report");
    } else {
      setValue(storedTab || "user_expense_report");
    }
  }, [userRole]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    localStorage.setItem(TAB_STORAGE_KEY, newValue);
  };

  if (loading || !userRole?.role || !userDetails?.official_email) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fff",
        }}
      >
        <CircularProgress size={56} color="primary" />
      </Box>
    );
  }

  if (!isAllowedDomain) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fef6f6",
          textAlign: "center",
          p: 4,
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          You are not logged in with an authorized email domain.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack sx={{ position: "relative" }}>
        <TabContext value={value}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
            mb={2}
          >
            <Box sx={{ flex: "0 0 auto", minWidth: "200px" }}>
              <Typography variant="h5" fontWeight={550}>
                Expense Confirmation Report
              </Typography>
            </Box>
            <Box
              sx={{
                position: "relative",
                width: { xs: "100%", md: "25%" },
                zIndex: 10,
              }}
            >
              <Accordion
                disableGutters
                sx={{
                  borderRadius: 2,
                  boxShadow: 3,
                  position: "relative",
                  overflow: "visible",
                  border: "1px solid #d9e2ec",
                  backgroundColor: "#d9e2ec",
                  zIndex: 20,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: "#d9e2ec",
                    borderRadius: "12px 12px 0 0",
                    color: "#000",
                    "& .MuiTypography-root": {
                      fontWeight: 600,
                    },
                  }}
                >
                  <Typography display="flex" alignItems="center">
                    Important Note
                    <InfoOutlinedIcon
                      sx={{ color: "blue", fontSize: 20, ml: 1 }}
                    />
                  </Typography>
                </AccordionSummary>
                <Divider sx={{ borderColor: "#f0e8cc", opacity: 0.7 }} />
                <AccordionDetails
                  sx={{
                    position: "absolute",
                    borderColor: "#f0e8cc",
                    top: "100%",
                    left: 0,
                    zIndex: 1000,
                    bgcolor: "#f0e8cc",
                    borderRadius: "0 0 12px 12px",
                    boxShadow: 3,
                    padding: 2,
                    overflowX: "hidden",
                    color: "#000",
                  }}
                >
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    <li>
                      <Typography variant="body2">
                        <strong>Deadline Alert</strong>: After the deadline, if
                        your manager’s approval is pending, the
                        system-calculated amount will be considered.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Total Expense Amount</strong> = (DA + TA) Or (Expected + Extra Amount) + food + fix + hill station allowance
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>System Total Amount</strong> = DA + TA
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Total Final Amount</strong> = (DA + TA) Or
                        (Expected + Extra Amount)
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Late/Missing Reporting: </strong> If daily
                        reporting is not done before expense generation, you
                        will not receive the expense for that particular day.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Correction Restricted: </strong> If the system
                        remark is "Brick Not Found" or "Work Location &
                        Check-Out Missing", you will not be able to correct the
                        expense for that day.
                      </Typography>
                    </li>
                  </ul>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <TabList onChange={handleChange}>
            <Tab label="My Expense Report" value="user_expense_report" />
            {!isAreaManager && (
              <Tab
                label="My Team Expense Approval"
                value="manager_expense_approval"
              />
            )}
          </TabList>
          <TabPanel sx={{ p: 0 }} value="user_expense_report">
            <UserExpenseReport />
          </TabPanel>
          {!isAreaManager && (
            <TabPanel sx={{ p: 0 }} value="manager_expense_approval">
              <ManagerExpenseApproval />
            </TabPanel>
          )}
        </TabContext>
      </Stack>
    </>
  );
};

export default ExpenseDashBoardDesktop;
